import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Battery, Moon, Clock, Sparkles, Loader2, Check, X, 
  Heart, Zap, Coffee, Sun, BedDouble, TrendingUp, AlertTriangle, Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const energyLabels = ['Exhausted', 'Low', 'Moderate', 'Good', 'Energized'];
const sleepQualityLabels = ['Terrible', 'Poor', 'Fair', 'Good', 'Great'];

const WellnessPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [todayWellness, setTodayWellness] = useState(null);
  const [activeTab, setActiveTab] = useState('checkin');
  
  // Form state
  const [energyLevel, setEnergyLevel] = useState(3);
  const [sleepQuality, setSleepQuality] = useState(3);
  const [sleepHours, setSleepHours] = useState(7);
  const [timeForSelf, setTimeForSelf] = useState(0);
  const [notes, setNotes] = useState('');
  
  // Propagation state
  const [propagationConfirmed, setPropagationConfirmed] = useState(false);
  
  // Burnout signal
  const [burnoutSignal, setBurnoutSignal] = useState(null);
  
  // Wellness Inference state
  // DISABLED: Built ahead of July 5 decision to defer until post-E8 with real cohort data
  // Endpoint /api/wellness/inferred exists but UI is hidden pending explicit approval
  const [inferredSuggestion, setInferredSuggestion] = useState(null);
  const [showManualEntry, setShowManualEntry] = useState(true); // Default to manual entry
  const [loadingInference, setLoadingInference] = useState(false);
  const WELLNESS_INFERENCE_ENABLED = false; // Feature flag - set to true when approved
  
  // History/Patterns state
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Micro-routine state
  const [generatingRoutine, setGeneratingRoutine] = useState(false);
  const [routines, setRoutines] = useState([]);
  const [routineCallId, setRoutineCallId] = useState(null);
  const [timeAvailable, setTimeAvailable] = useState(5);

  const fetchTodayWellness = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/wellness/today`);
      if (response.data) {
        setTodayWellness(response.data);
        setEnergyLevel(response.data.energy_level || 3);
        setSleepQuality(response.data.sleep_quality || 3);
        setSleepHours(response.data.sleep_hours || 7);
        setTimeForSelf(response.data.time_for_self_mins || 0);
        setNotes(response.data.notes || '');
        setPropagationConfirmed(response.data.propagation_confirmed || false);
        setBurnoutSignal(response.data.burnout_signal || null);
        setShowManualEntry(true); // Already logged today, show manual mode
      } else {
        // No log today - fetch inference
        fetchInferredWellness();
      }
    } catch (error) {
      console.error('Failed to fetch wellness:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Wellness Inference: Fetch inferred wellness from signals
  // DISABLED: Feature hidden pending explicit approval (July 5 decision)
  const fetchInferredWellness = async () => {
    if (!WELLNESS_INFERENCE_ENABLED) {
      setShowManualEntry(true);
      return;
    }
    setLoadingInference(true);
    try {
      const response = await axios.get(`${API}/wellness/inferred`);
      if (response.data.has_signals && response.data.suggestion) {
        setInferredSuggestion(response.data.suggestion);
        // Pre-fill sliders with inferred values
        setEnergyLevel(response.data.suggestion.energy_level);
        setSleepQuality(response.data.suggestion.sleep_quality);
        setSleepHours(response.data.suggestion.sleep_hours);
      } else {
        // No signals, show manual entry
        setShowManualEntry(true);
      }
    } catch (error) {
      console.error('Failed to fetch inferred wellness:', error);
      setShowManualEntry(true);
    } finally {
      setLoadingInference(false);
    }
  };

  // Accept inferred wellness
  const acceptInferred = async () => {
    if (!inferredSuggestion) return;
    setSaving(true);
    try {
      await axios.post(`${API}/wellness/log`, {
        energy_level: inferredSuggestion.energy_level,
        sleep_quality: inferredSuggestion.sleep_quality,
        sleep_hours: inferredSuggestion.sleep_hours,
        time_for_self_mins: 0,
        notes: `Inferred from: ${inferredSuggestion.signals.join(', ')}`,
        source: 'inferred',
        inferred_from: inferredSuggestion.inferred_from
      });
      toast.success('Wellness logged from signals!');
      setInferredSuggestion(null);
      fetchTodayWellness();
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Edit inferred → switch to manual with pre-filled values
  const editInferred = () => {
    setShowManualEntry(true);
  };

  // Dismiss inferred → show empty manual entry
  const dismissInferred = () => {
    setInferredSuggestion(null);
    setEnergyLevel(3);
    setSleepQuality(3);
    setSleepHours(7);
    setTimeForSelf(0);
    setShowManualEntry(true);
  };

  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/wellness/history?days=14`);
      setHistory(response.data.history || []);
      setSummary(response.data.summary || null);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    fetchTodayWellness();
  }, [fetchTodayWellness]);

  useEffect(() => {
    if (activeTab === 'patterns') {
      fetchHistory();
    }
  }, [activeTab, fetchHistory]);

  const handleSaveWellness = async (source = 'manual') => {
    setSaving(true);
    try {
      await axios.post(`${API}/wellness/log`, {
        energy_level: energyLevel,
        sleep_quality: sleepQuality,
        sleep_hours: sleepHours,
        time_for_self_mins: timeForSelf,
        notes: notes || null,
        source: source,
        inferred_from: source === 'inferred' ? inferredSuggestion?.inferred_from : null
      });
      toast.success('Wellness logged!');
      setInferredSuggestion(null);
      fetchTodayWellness();
    } catch (error) {
      console.error('Wellness save error:', error.response?.data || error.message);
      const errMsg = error.response?.data?.detail || 'Failed to save wellness';
      toast.error(errMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmPropagation = async () => {
    try {
      await axios.post(`${API}/wellness/confirm-propagation`);
      setPropagationConfirmed(true);
      toast.success('AI suggestions will now adapt to your energy level');
    } catch (error) {
      toast.error('Failed to confirm');
    }
  };

  const handleGenerateRoutine = async () => {
    setGeneratingRoutine(true);
    try {
      const response = await axios.post(`${API}/ai/wellness/micro-routine`, {
        energy_level: energyLevel,
        time_available_mins: timeAvailable,
        context: new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'
      });
      setRoutines(response.data.routines);
      setRoutineCallId(response.data.call_id);
    } catch (error) {
      toast.error('Failed to generate routines');
    } finally {
      setGeneratingRoutine(false);
    }
  };

  const confirmAIAction = async (callId, userAction) => {
    if (!callId) return;
    try {
      await axios.post(`${API}/ai/confirm`, {
        call_id: callId,
        user_action: userAction,
        edit_delta: []
      });
    } catch (error) {
      console.error('Failed to confirm AI action');
    }
  };

  const acceptRoutine = (routine) => {
    toast.success(`Starting: ${routine.title}`);
    confirmAIAction(routineCallId, 'accepted');
    setRoutines([]);
    setRoutineCallId(null);
  };

  const dismissRoutines = () => {
    confirmAIAction(routineCallId, 'ignored');
    setRoutines([]);
    setRoutineCallId(null);
  };

  const getEnergyColor = (level) => {
    if (level <= 2) return 'text-red-500';
    if (level <= 3) return 'text-amber-500';
    return 'text-green-500';
  };

  const getEnergyBgColor = (level) => {
    if (level <= 2) return 'bg-red-500';
    if (level <= 3) return 'bg-amber-500';
    return 'bg-green-500';
  };

  const getEnergyIcon = (level) => {
    if (level <= 1) return <Battery className="w-5 h-5 text-red-500" />;
    if (level <= 2) return <Coffee className="w-5 h-5 text-amber-500" />;
    if (level <= 3) return <Sun className="w-5 h-5 text-yellow-500" />;
    if (level <= 4) return <Zap className="w-5 h-5 text-green-500" />;
    return <Sparkles className="w-5 h-5 text-emerald-500" />;
  };

  // Format date for display
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-12 bg-nurture-cream-dark rounded-xl animate-pulse" />
        <div className="h-64 bg-nurture-cream-dark rounded-3xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="wellness-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-nurture-navy flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
            <Heart className="w-5 h-5 text-emerald-600" />
          </div>
          Wellness Check-In
        </h1>
        <p className="text-nurture-muted mt-1">How are you feeling today? This helps AI adapt suggestions to your energy.</p>
      </div>

      {/* Burnout Alert */}
      {burnoutSignal && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className={`rounded-2xl border-2 ${burnoutSignal.level === 'high' ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50'}`}>
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${burnoutSignal.level === 'high' ? 'bg-red-100' : 'bg-amber-100'}`}>
                  <AlertTriangle className={`w-5 h-5 ${burnoutSignal.level === 'high' ? 'text-red-500' : 'text-amber-500'}`} />
                </div>
                <div className="flex-1">
                  <p className={`font-medium ${burnoutSignal.level === 'high' ? 'text-red-700' : 'text-amber-700'}`}>
                    {burnoutSignal.level === 'high' ? 'Burnout Warning' : 'Energy Check'}
                  </p>
                  <p className="text-sm text-nurture-muted">{burnoutSignal.message}</p>
                </div>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setActiveTab('checkin')}
                  className="rounded-lg"
                >
                  Get Help
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 rounded-xl">
          <TabsTrigger value="checkin" className="rounded-lg">Today's Check-In</TabsTrigger>
          <TabsTrigger value="patterns" className="rounded-lg">My Patterns</TabsTrigger>
        </TabsList>

        {/* Check-In Tab */}
        <TabsContent value="checkin" className="space-y-6 mt-4">
          {/* Wellness Inference Suggestion Card - HIDDEN pending approval
              Built ahead of July 5 decision to defer until post-E8 with real cohort data.
              Backend endpoint /api/wellness/inferred exists but UI disabled.
              Set WELLNESS_INFERENCE_ENABLED = true to re-enable after approval. */}
          {WELLNESS_INFERENCE_ENABLED && loadingInference ? (
            <Card className="rounded-3xl shadow-nurture border-2 border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50">
              <CardContent className="py-8">
                <div className="flex items-center justify-center gap-3">
                  <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
                  <span className="text-indigo-700">Analyzing your recent activity...</span>
                </div>
              </CardContent>
            </Card>
          ) : WELLNESS_INFERENCE_ENABLED && inferredSuggestion && !showManualEntry ? (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="rounded-3xl shadow-nurture border-2 border-indigo-300 bg-gradient-to-r from-indigo-50 to-purple-50" data-testid="inferred-suggestion-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-indigo-500" />
                    We noticed something...
                    <Badge className="bg-indigo-100 text-indigo-700 ml-auto">AI Inference</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-nurture-muted">
                    Based on your recent activity, here's what we inferred about your wellness today:
                  </p>
                  
                  {/* Inferred Values Preview */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 bg-white rounded-xl text-center">
                      <div className="flex items-center justify-center mb-2">
                        <Zap className={`w-6 h-6 ${getEnergyColor(inferredSuggestion.energy_level)}`} />
                      </div>
                      <p className="text-2xl font-bold text-nurture-navy">{inferredSuggestion.energy_level}/5</p>
                      <p className="text-xs text-nurture-muted">Energy</p>
                    </div>
                    <div className="p-4 bg-white rounded-xl text-center">
                      <Moon className="w-6 h-6 text-indigo-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-nurture-navy">{inferredSuggestion.sleep_quality}/5</p>
                      <p className="text-xs text-nurture-muted">Sleep Quality</p>
                    </div>
                    <div className="p-4 bg-white rounded-xl text-center">
                      <BedDouble className="w-6 h-6 text-purple-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-nurture-navy">{inferredSuggestion.sleep_hours}h</p>
                      <p className="text-xs text-nurture-muted">Sleep Hours</p>
                    </div>
                  </div>

                  {/* Signals Used */}
                  {inferredSuggestion.signals && inferredSuggestion.signals.length > 0 && (
                    <div className="p-3 bg-white/50 rounded-xl">
                      <p className="text-sm text-nurture-muted">
                        <span className="font-medium text-nurture-navy">Inferred from:</span>{' '}
                        {inferredSuggestion.signals.join(', ')}
                      </p>
                    </div>
                  )}

                  {/* HITL Action Buttons */}
                  <div className="flex flex-wrap gap-2 pt-2">
                    <Button
                      onClick={acceptInferred}
                      disabled={saving}
                      className="bg-nurture-sage hover:bg-nurture-sage-hover rounded-xl flex-1 sm:flex-none"
                      data-testid="accept-inferred-btn"
                    >
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                      Accept
                    </Button>
                    <Button
                      variant="outline"
                      onClick={editInferred}
                      className="rounded-xl flex-1 sm:flex-none"
                      data-testid="edit-inferred-btn"
                    >
                      Edit Values
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={dismissInferred}
                      className="rounded-xl text-nurture-muted"
                      data-testid="dismiss-inferred-btn"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Dismiss
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ) : null}

          {/* Manual Entry Toggle - HIDDEN: Inference feature disabled
              Show when inference was dismissed or edited - only if feature enabled */}
          {WELLNESS_INFERENCE_ENABLED && !inferredSuggestion && !todayWellness && !loadingInference && (
            <div className="flex items-center justify-center">
              <Button
                variant="ghost"
                onClick={() => fetchInferredWellness()}
                className="text-nurture-muted text-sm"
                data-testid="retry-inference-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Try AI inference again
              </Button>
            </div>
          )}

          {/* Main Wellness Card - Always show manual entry since inference is disabled */}
          {(showManualEntry || todayWellness) && (
          <Card className="rounded-3xl shadow-nurture">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {getEnergyIcon(energyLevel)}
                Today's Check-In
                {todayWellness && (
                  <Badge className="bg-nurture-sage/20 text-nurture-sage ml-auto">Logged</Badge>
                )}
                {!todayWellness && inferredSuggestion && (
                  <Badge className="bg-indigo-100 text-indigo-600 ml-auto">Editing inferred values</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Energy Level */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-amber-500" />
                    Energy Level
                  </Label>
                  <span className={`font-semibold ${getEnergyColor(energyLevel)}`}>
                    {energyLabels[energyLevel - 1]} ({energyLevel}/5)
                  </span>
                </div>
                <Slider
                  value={[energyLevel]}
                  onValueChange={(v) => setEnergyLevel(v[0])}
                  min={1}
                  max={5}
                  step={1}
                  className="w-full"
                  data-testid="energy-slider"
                />
                <div className="flex justify-between text-xs text-nurture-muted">
                  <span>Exhausted</span>
                  <span>Energized</span>
                </div>
              </div>

              {/* Sleep Quality */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <Moon className="w-4 h-4 text-indigo-500" />
                    Sleep Quality
                  </Label>
                  <span className="font-semibold text-nurture-navy">
                    {sleepQualityLabels[sleepQuality - 1]} ({sleepQuality}/5)
                  </span>
                </div>
                <Slider
                  value={[sleepQuality]}
                  onValueChange={(v) => setSleepQuality(v[0])}
                  min={1}
                  max={5}
                  step={1}
                  className="w-full"
                  data-testid="sleep-quality-slider"
                />
              </div>

              {/* Sleep Hours */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <BedDouble className="w-4 h-4 text-purple-500" />
                    Hours Slept
                  </Label>
                  <span className="font-semibold text-nurture-navy">{sleepHours} hours</span>
                </div>
                <Slider
                  value={[sleepHours]}
                  onValueChange={(v) => setSleepHours(v[0])}
                  min={3}
                  max={12}
                  step={0.5}
                  className="w-full"
                  data-testid="sleep-hours-slider"
                />
                <div className="flex justify-between text-xs text-nurture-muted">
                  <span>3h</span>
                  <span>12h</span>
                </div>
              </div>

              {/* Time for Self */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-pink-500" />
                    Time for Yourself Today
                  </Label>
                  <span className="font-semibold text-nurture-navy">{timeForSelf} mins</span>
                </div>
                <Slider
                  value={[timeForSelf]}
                  onValueChange={(v) => setTimeForSelf(v[0])}
                  min={0}
                  max={120}
                  step={5}
                  className="w-full"
                  data-testid="time-for-self-slider"
                />
                <div className="flex justify-between text-xs text-nurture-muted">
                  <span>None</span>
                  <span>2 hours</span>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Anything else affecting your day? Headache, stress, good news..."
                  className="rounded-xl"
                  rows={2}
                  data-testid="wellness-notes"
                />
              </div>

              {/* Save Button */}
              <Button
                onClick={() => handleSaveWellness('manual')}
                disabled={saving}
                className="w-full bg-nurture-terracotta hover:bg-nurture-terracotta-hover rounded-xl"
                data-testid="save-wellness-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : todayWellness ? (
                  'Update Check-In'
                ) : (
                  'Log Check-In'
                )}
              </Button>
            </CardContent>
          </Card>
          )}

          {/* Propagation Confirmation */}
          {todayWellness && !propagationConfirmed && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="rounded-3xl shadow-nurture border-2 border-nurture-sage/30 bg-gradient-to-r from-nurture-sage/10 to-emerald-50">
                <CardContent className="py-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-nurture-sage/20 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-6 h-6 text-nurture-sage" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading font-semibold text-nurture-navy mb-1">
                        Activate Smart Suggestions?
                      </h3>
                      <p className="text-sm text-nurture-muted mb-4">
                        Your energy level is <span className={`font-semibold ${getEnergyColor(energyLevel)}`}>{energyLabels[energyLevel - 1].toLowerCase()}</span> today. 
                        Would you like AI suggestions (meals, routines, etc.) to adapt accordingly?
                      </p>
                      <div className="flex gap-2">
                        <Button
                          onClick={handleConfirmPropagation}
                          className="bg-nurture-sage hover:bg-nurture-sage-hover rounded-xl"
                          data-testid="confirm-propagation-btn"
                        >
                          <Check className="w-4 h-4 mr-2" />
                          Yes, adapt suggestions
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setPropagationConfirmed(true)}
                          className="rounded-xl"
                        >
                          Not today
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Micro-Routine Generator */}
          <Card className="rounded-3xl shadow-nurture">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-nurture-terracotta" />
                Quick Self-Care Routine
                <Badge className="bg-nurture-terracotta/20 text-nurture-terracotta ml-2">AI</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-nurture-muted">
                Get a quick self-care micro-routine based on your energy level and available time.
              </p>
              
              {/* Time Available */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Time available</Label>
                  <span className="font-semibold text-nurture-navy">{timeAvailable} mins</span>
                </div>
                <Slider
                  value={[timeAvailable]}
                  onValueChange={(v) => setTimeAvailable(v[0])}
                  min={3}
                  max={30}
                  step={1}
                  className="w-full"
                  data-testid="time-available-slider"
                />
              </div>

              <Button
                onClick={handleGenerateRoutine}
                disabled={generatingRoutine}
                className="w-full bg-nurture-terracotta hover:bg-nurture-terracotta-hover rounded-xl"
                data-testid="generate-routine-btn"
              >
                {generatingRoutine ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Finding routines...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Suggest Micro-Routine
                  </>
                )}
              </Button>

              {/* Generated Routines */}
              <AnimatePresence>
                {routines.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="space-y-3 pt-4"
                  >
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-nurture-navy">Suggested Routines</h4>
                      <Button variant="ghost" size="sm" onClick={dismissRoutines}>
                        <X className="w-4 h-4 mr-1" /> Dismiss
                      </Button>
                    </div>
                    
                    {routines.map((routine, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="p-4 bg-gradient-to-r from-nurture-cream to-purple-50 rounded-xl border border-nurture-cream-dark"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h5 className="font-semibold text-nurture-navy">{routine.title}</h5>
                              <Badge variant="outline" className="text-xs">
                                {routine.duration_mins} min
                              </Badge>
                            </div>
                            <ul className="text-sm text-nurture-muted space-y-1 mb-2">
                              {routine.steps?.map((step, i) => (
                                <li key={i}>• {step}</li>
                              ))}
                            </ul>
                            <p className="text-xs text-nurture-sage italic">{routine.benefit}</p>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => acceptRoutine(routine)}
                            className="bg-nurture-sage hover:bg-nurture-sage-hover rounded-lg"
                            data-testid={`accept-routine-${index}`}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Patterns Tab */}
        <TabsContent value="patterns" className="space-y-6 mt-4">
          {loadingHistory ? (
            <div className="space-y-4">
              <div className="h-32 bg-nurture-cream-dark rounded-3xl animate-pulse" />
              <div className="h-64 bg-nurture-cream-dark rounded-3xl animate-pulse" />
            </div>
          ) : (
            <>
              {/* Summary Stats */}
              {summary && (
                <Card className="rounded-3xl shadow-nurture bg-gradient-to-r from-purple-50 to-indigo-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-indigo-500" />
                      14-Day Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 bg-white rounded-xl text-center">
                        <p className="text-2xl font-bold text-nurture-navy">{summary.avg_energy}</p>
                        <p className="text-xs text-nurture-muted">Avg Energy</p>
                      </div>
                      <div className="p-4 bg-white rounded-xl text-center">
                        <p className="text-2xl font-bold text-nurture-navy">{summary.avg_sleep_hours}h</p>
                        <p className="text-xs text-nurture-muted">Avg Sleep</p>
                      </div>
                      <div className="p-4 bg-white rounded-xl text-center">
                        <p className="text-2xl font-bold text-green-500">{summary.good_energy_days}</p>
                        <p className="text-xs text-nurture-muted">Good Days</p>
                      </div>
                      <div className="p-4 bg-white rounded-xl text-center">
                        <p className="text-2xl font-bold text-red-500">{summary.low_energy_days}</p>
                        <p className="text-xs text-nurture-muted">Low Days</p>
                      </div>
                    </div>
                    <div className="mt-4 p-3 bg-white/50 rounded-xl">
                      <p className="text-sm text-nurture-muted">
                        <span className="font-medium text-nurture-navy">Self-care time:</span> {summary.total_self_time_mins} mins total over {summary.days_logged} days logged
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Energy Dot Chart */}
              <Card className="rounded-3xl shadow-nurture">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-nurture-terracotta" />
                    Energy Pattern
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {history.length === 0 ? (
                    <div className="text-center py-8">
                      <Moon className="w-12 h-12 mx-auto text-nurture-muted/30 mb-3" />
                      <p className="text-nurture-muted">No wellness logs yet.</p>
                      <p className="text-sm text-nurture-muted">Start logging to see your patterns!</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {/* Dot Chart */}
                      <div className="flex flex-wrap gap-2 justify-center mb-4">
                        {history.map((day, idx) => (
                          <div 
                            key={idx} 
                            className="group relative"
                            title={`${formatDate(day.date)}: Energy ${day.energy_level}/5`}
                          >
                            <div 
                              className={`w-8 h-8 rounded-full ${getEnergyBgColor(day.energy_level)} flex items-center justify-center text-white text-xs font-bold cursor-pointer hover:scale-110 transition-transform`}
                              data-testid={`energy-dot-${idx}`}
                            >
                              {day.energy_level}
                            </div>
                            {/* Tooltip */}
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-nurture-navy text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                              {formatDate(day.date)}
                              <br />
                              Sleep: {day.sleep_hours}h
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Legend */}
                      <div className="flex justify-center gap-4 text-xs text-nurture-muted">
                        <span className="flex items-center gap-1">
                          <div className="w-3 h-3 rounded-full bg-red-500" /> 1-2 Low
                        </span>
                        <span className="flex items-center gap-1">
                          <div className="w-3 h-3 rounded-full bg-amber-500" /> 3 Moderate
                        </span>
                        <span className="flex items-center gap-1">
                          <div className="w-3 h-3 rounded-full bg-green-500" /> 4-5 Good
                        </span>
                      </div>

                      {/* History List */}
                      <div className="mt-6 space-y-2 max-h-64 overflow-y-auto">
                        {[...history].reverse().map((day, idx) => (
                          <div 
                            key={idx}
                            className="flex items-center justify-between p-3 bg-nurture-cream/50 rounded-xl"
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full ${getEnergyBgColor(day.energy_level)} flex items-center justify-center text-white text-sm font-bold`}>
                                {day.energy_level}
                              </div>
                              <div>
                                <p className="font-medium text-nurture-navy text-sm">{formatDate(day.date)}</p>
                                <p className="text-xs text-nurture-muted">
                                  {day.sleep_hours}h sleep • {day.time_for_self_mins || 0} mins self-care
                                </p>
                              </div>
                            </div>
                            <Badge variant="outline" className="text-xs">
                              {energyLabels[day.energy_level - 1]}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default WellnessPage;
